import React, { useEffect, useState } from "react";
import { getBanks } from "../../../../services/dashboard/banks";
import { getBankTransactions } from "../../../../services/Accounts/Safes";
import CreateBankForm from "./Actions/createBank ";
import BankStatement from "./BankStatement/BankStatement";
import EditBankForm from "./Actions/EditBankForm";
import { useFeatures } from "../../../../context/PermissionsAndFeatures/FeaturesContext";


import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Grid,
  Divider,
  Snackbar,
  Alert,
  TextField,
  Paper,
  Tooltip,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import LanIcon from "@mui/icons-material/Lan";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";

const BanksList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  const [filterBranchName, setFilterBranchName] = useState("");
  const [branchOptions, setBranchOptions] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedBank, setSelectedBank] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [branchTarget, setBranchTarget] = useState(null);
  const [unlinkMultiTarget, setUnlinkMultiTarget] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [bankTotals, setBankTotals] = useState({}); // تخزين الإجماليات لكل بنك
  const { features } = useFeatures();
  const cashboxLimit = Number(features?.cashboxes || 0);
  const activeCashboxesCount = banks.filter((b) => b.type === "bank").length;
  const isCashboxLimitReached =
  tabIndex === 0 && cashboxLimit > 0 && activeCashboxesCount >= cashboxLimit;


  const fetchBanks = async () => {
    try {
      const response = await getBanks();
      setBanks(response);

      // استخراج جميع أسماء الفروع الموجودة لتستخدم في الفلتر
      const uniqueBranches = new Set();
      response.forEach((bank) => {
        bank.roots?.forEach((r) => uniqueBranches.add(r.name));
      });
      setBranchOptions(Array.from(uniqueBranches));

      // جلب الإجماليات لكل بنك/خزنة
      const totalsObj = {};
      await Promise.all(
        response.map(async (bank) => {
          try {
            const res = await getBankTransactions(bank.id);
            totalsObj[bank.id] = res?.totals || {};
          } catch {
            totalsObj[bank.id] = {};
          }
        })
      );
      setBankTotals(totalsObj);
    } catch (err) {
      setError("❌ فشل في تحميل البيانات.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
  }, []);

  const mainBanks = banks.filter(
    (b) =>
      b.type === "bank_account" &&
      (!filterBranchName || b.roots?.some((r) => r.name === filterBranchName))
  );
  const subSafes = banks.filter(
    (b) =>
      b.type === "bank" &&
      (!filterBranchName || b.roots?.some((r) => r.name === filterBranchName))
  );

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleBankClick = (bank) => {
    setSelectedBank(bank);
  };

  const renderBankCard = (bank) => (
    <Grid item xs={12} sm={6} md={6} lg={4} key={bank.id}>
      <Card
        variant="outlined"
        sx={(theme) => ({
          height: "100%",
          position: "relative",
          boxShadow: isMobile ? 1 : 2,
          transition: "0.3s",
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          ":hover": { boxShadow: isMobile ? 3 : 6 }
        })}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              fontWeight="bold"
              sx={{ fontSize: { xs: "14px", sm: "16px", md: "18px" } }}
            >
              {bank.name}
            </Typography>
            <Box display="flex" gap={0.5}>
              <Tooltip title="عرض كشف الحساب">
                <IconButton 
                  onClick={() => handleBankClick(bank)} 
                  sx={{ color: "#0288d1", p: { xs: 0.5, sm: 1 } }}
                  size={isMobile ? "small" : "medium"}
                >
                  <VisibilityIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="تعديل الحساب">
                <IconButton 
                  onClick={() => setEditTarget(bank)} 
                  sx={{ color: "#f9a825", p: { xs: 0.5, sm: 1 } }}
                  size={isMobile ? "small" : "medium"}
                >
                  <EditIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Divider sx={{ mb: { xs: 0.5, sm: 1 } }} />
          <Typography 
            variant="body2" 
            sx={{ mb: 0.5, fontSize: { xs: "12px", sm: "13px", md: "14px" } }}
          >
            رقم الحساب: <strong>{bank.account_number || "غير محدد"}</strong>
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ mb: 0.5, fontSize: { xs: "12px", sm: "13px", md: "14px" } }}
          >
            النوع: {bank.type === "bank" ? "خزنة" : "بنك"}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ mb: 0.5, fontSize: { xs: "12px", sm: "13px", md: "14px" } }}
          >
            نسبة الخصم: {bank.discount_percentage}%
          </Typography>
          <Typography 
            variant="body2"
            sx={{ fontSize: { xs: "12px", sm: "13px", md: "14px" } }}
          >
            الفروع المرتبطة:
            {bank.roots?.length > 0
              ? bank.roots.map((r, i) => (
                  <span key={r.id} style={{ marginLeft: 4 }}>
                    {r.name}
                    {i < bank.roots.length - 1 ? "," : ""}
                  </span>
                ))
              : <span style={{ color: "#888" }}>غير مرتبط بأي فرع</span>}
          </Typography>
          {bankTotals[bank.id] && (
            <Box
              mt={{ xs: 1, sm: 2 }}
              sx={(theme) => ({
                bgcolor: theme.palette.mode === "dark" ? "#23272f" : "#f5f5f5",
                borderRadius: 2,
                p: { xs: 0.75, sm: 1 }
              })}
            >
              <Typography 
                variant="subtitle2" 
                fontWeight="bold" 
                color="primary" 
                mb={0.5}
                sx={{ fontSize: { xs: "12px", sm: "13px", md: "14px" } }}
              >
                الإجمالي:
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight="bold" 
                color="success.main"
                sx={{ fontSize: { xs: "13px", sm: "14px", md: "15px" } }}
              >
                {((bankTotals[bank.id].total_income ?? 0) - (bankTotals[bank.id].total_out ?? 0)).toLocaleString()}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box
      p={{ xs: 1, sm: 2, md: 3 }}
      sx={(theme) => ({
        background: theme.palette.mode === "dark"
          ? "linear-gradient(135deg, #23272f 0%, #1a222b 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #e3f2fd 100%)",
        minHeight: "100vh"
      })}
    >
      <Paper
        elevation={isMobile ? 2 : 3}
        sx={(theme) => ({
          p: { xs: 1, sm: 1.5, md: 2 },
          mb: { xs: 2, md: 3 },
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 2 },
          background: theme.palette.mode === "dark"
            ? "linear-gradient(90deg, #23272f 60%, #222b36 100%)"
            : "linear-gradient(90deg, #e3f2fd 60%, #fff 100%)",
          borderRadius: { xs: 2, md: 3 },
          boxShadow: theme.palette.mode === "dark"
            ? "0 2px 12px 0 rgba(2,136,209,0.12)"
            : "0 2px 12px 0 rgba(2,136,209,0.08)"
        })}
      >
        <Select
          displayEmpty
          fullWidth
          size="small"
          value={filterBranchName}
          onChange={(e) => setFilterBranchName(e.target.value)}
          sx={(theme) => ({
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            fontWeight: "bold",
            color: theme.palette.text.primary,
            fontSize: { xs: "13px", sm: "14px" },
            boxShadow: theme.palette.mode === "dark"
              ? "0 1px 4px 0 rgba(2,136,209,0.10)"
              : "0 1px 4px 0 rgba(2,136,209,0.05)"
          })}
        >
          <MenuItem value="" sx={{ fontWeight: "bold", fontSize: { xs: "13px", sm: "14px" } }}>
            كل الفروع
          </MenuItem>
          {branchOptions.map((name) => (
            <MenuItem key={name} value={name} sx={{ fontSize: { xs: "13px", sm: "14px" } }}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </Paper>

      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        mb={2}
        gap={{ xs: 1.5, sm: 0 }}
        borderBottom={1}
        borderColor="divider"
        sx={(theme) => ({
          pb: { xs: 1, sm: 1.5 },
          background: theme.palette.background.paper,
          borderRadius: 2,
          p: { xs: 1, sm: 1.5 },
          boxShadow: theme.palette.mode === "dark"
            ? "0 1px 8px 0 rgba(2,136,209,0.14)"
            : "0 1px 8px 0 rgba(2,136,209,0.06)"
        })}
      >
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
          sx={{
            width: { xs: "100%", sm: "auto" },
            ".MuiTab-root": {
              fontWeight: "bold",
              fontSize: { xs: "0.85rem", sm: "0.95rem", md: "1.1rem" },
              letterSpacing: 0.5,
              px: { xs: 1.5, sm: 2, md: 3 },
              minHeight: { xs: 42, sm: 48 },
            }
          }}
        >
          <Tab label={isMobile ? "💰 خزن" : "💰 الخزن"} />
          <Tab label={isMobile ? "🏦 بنوك" : "🏦 البنوك"} />
        </Tabs>
        
        {isCashboxLimitReached ? (
          <Alert 
            severity="warning" 
            sx={{ 
              my: { xs: 0, sm: 2 },
              width: { xs: "100%", sm: "auto" },
              fontSize: { xs: "12px", sm: "14px" },
              py: { xs: 0.5, sm: 1 }
            }}
          >
            {isMobile 
              ? `⚠️ الحد الأقصى: ${cashboxLimit} خزنة`
              : `⚠️ لقد وصلت إلى الحد الأقصى لعدد الخزن المسموح به (${cashboxLimit} خزنة)`
            }
          </Alert>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenDialog(true)}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{
              fontWeight: "bold",
              borderRadius: 2,
              boxShadow: "0 2px 8px 0 rgba(2,136,209,0.12)",
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 0.75, sm: 1 },
              fontSize: { xs: "0.85rem", sm: "0.9rem", md: "1rem" },
              mt: { xs: 0, sm: 0 }
            }}
          >
            {isMobile 
              ? (tabIndex === 0 ? "➕ خزنة جديدة" : "➕ بنك جديد")
              : (tabIndex === 0 ? "➕ إنشاء خزنة جديدة" : "➕ إنشاء بنك جديد")
            }
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress 
            size={isMobile ? 36 : 48} 
            thickness={5} 
            sx={{ color: "#0288d1" }} 
          />
        </Box>
      ) : (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} mt={0.5}>
          {(tabIndex === 0 ? subSafes : mainBanks).map(renderBankCard)}
        </Grid>
      )}

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "90vh" }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: "16px", sm: "18px", md: "20px" }, py: { xs: 1.5, sm: 2 } }}>
          {tabIndex === 0 ? "إنشاء خزنة جديدة" : "إنشاء بنك جديد"}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <CreateBankForm
            type={tabIndex === 0 ? "bank" : "bank_account"}
            onSuccess={(newBank) => {
              setOpenDialog(false);
              setBanks((prev) => [newBank, ...prev]);
              setSuccessMsg("✅ تم إنشاء الحساب بنجاح");
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog 
        open={!!selectedBank} 
        onClose={() => setSelectedBank(null)} 
        maxWidth="xl" 
        fullWidth={!isMobile}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            // أكبر عرض للـ dialog على الشاشات الكبيرة، مع استجابة للشاشات المتوسطة/صغيرة
            width: { xs: "100%", sm: "92%", md: "85%", lg: "75%" },
            // رفع الحد الأقصى للارتفاع ليشغل نسبة أكبر من الشاشة
            maxHeight: { xs: "100%", sm: "95vh", md: "95vh" },
            // تمكين التمرير داخل الورقة عند الحاجة
            overflow: "hidden",
            borderRadius: { xs: 0, sm: 2 }
          }
        }}
      >

        <DialogContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, overflowY: "auto", minHeight: { sm: "60vh", md: "70vh" } }}>
          {selectedBank && <BankStatement bank={selectedBank} />}
        </DialogContent>
      </Dialog>

      {editTarget && (
        <EditBankForm
          bank={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={(updatedBank) => {
            setBanks((prev) => prev.map((b) => (b.id === updatedBank.id ? updatedBank : b)));
            setSuccessMsg("✅ تم تحديث البيانات بنجاح");
          }}
        />
      )}

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{ 
          vertical: isMobile ? "top" : "bottom", 
          horizontal: "center" 
        }}
      >
        <Alert 
          onClose={() => setSuccessMsg("")} 
          severity="success" 
          sx={{ 
            width: "100%",
            fontSize: { xs: "13px", sm: "14px" }
          }}
        >
          {successMsg}
        </Alert>
      </Snackbar>

      {error && (
        <Typography 
          color="error" 
          mt={2}
          sx={{ fontSize: { xs: "13px", sm: "14px" } }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default BanksList;
